import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_CASE_ACTIONS, MOCK_CASE_AUDIT } from '@/mocks/cases'
import { USE_MOCKS } from '@/mocks/fixtures'
import type {
  AuditEntry,
  CaseActionEntry,
  CaseHistoryEvent,
  Maybe,
} from '@/types/domain'

/* The case's past, from the two places that hold it (FR-37, UX-DR-12).
 *
 * TWO REQUESTS AND ONE MERGE HERE, not one composed endpoint on the server, and
 * that is a decision rather than an accident of what was easy:
 *
 *   - the sources differ in GRAIN. The action log records one act ("somebody
 *     changed the status"); the audit trail records one field transition
 *     ("actual cost 28 400.00 → 29 680.00"). A row that had to be both would
 *     carry a field of which half is always empty.
 *   - they differ in SUPPRESSION. The log carries no values and so needs no
 *     AD-6 rule at all; the trail carries classified ones and withholds them as
 *     a pair. A merged endpoint would have to pick one of the two models for
 *     rows that need both.
 *   - they differ in WRITE GUARANTEE. The log is written by middleware after
 *     the response and can be lost; the trail commits inside the transaction
 *     that caused it. Merging them server-side would put one shape of entry in
 *     a third place and hide that difference behind it.
 *
 * The practical dividend is the one the section is judged on: two queries are
 * two error states, so a dead action log still leaves the financial changes on
 * screen with a notice naming what is missing (AC 8). A composed endpoint fails
 * whole, and half a history that says nothing reads as the whole history.
 *
 * NO PAGINATION, and BOTH SOURCES ARE CAPPED. The trail is asked for at most
 * AUDIT_LIMIT entries; the log takes no limit parameter and comes back under a
 * cap the server chooses for it. Filtering and search run on the client over
 * what came back. This is a ten-user company with low thousands of cases a year
 * — one case's history is tens of entries. `auditTruncated` and
 * `actionsTruncated` exist for the day that stops being true: a cap that bites
 * silently reads as "this is everything".
 */

/** What the trail is asked for. Read by the component too, which says so out
 *  loud when exactly this many entries come back. */
export const AUDIT_LIMIT = 500

/** What the action log comes back capped at — NOT a parameter this file sends.
 *  The endpoint takes no limit and the server picks one (`forEntityLimit` in
 *  backend/platform/actionlog/actionlog.go), so this constant is a MIRROR of
 *  that number, kept here only so the section can notice the cap biting.
 *
 *  Should the two ever drift, the notice fires early or stays quiet by exactly
 *  the difference — neither of which drops an entry from the feed. What is not
 *  survivable is having no mirror at all, which is a feed that stops dead at
 *  five hundred acts and looks like a case with five hundred acts in it. */
export const ACTION_LIMIT = 500

/** casefile's ActionEntryDTO (internal/casefile/history.go). */
interface ActionEntryResponse {
  id: string
  occurredAt: string
  actorName: string
  action: string
  detail?: Record<string, string>
}

/** finance's AuditEntryDTO (internal/finance/dto.go).
 *
 *  Three shapes reach the two value fields and they are three different facts:
 *  a string is the value, `null` is "there was no previous value" (a field set
 *  for the first time), and `{"hidden":true,…}` is AD-6 withholding it from
 *  this caller. The mapper below collapses only the middle one — absent and
 *  never-set are the same thing to a reader — and passes the marker through
 *  untouched, because SuppressedValue is what knows how to draw it. */
interface AuditEntryResponse {
  id: string
  /** What record the change sits on (finance's RecordKind). The DTO's
   *  `recordId`, `caseId` and `actorId` are not mirrored: nothing here reads
   *  them, and the feed names records by kind, never by id. */
  recordKind: string
  actorName: string
  occurredAt: string
  field: string
  oldValue: Maybe<string | null>
  newValue: Maybe<string | null>
  /** Serialised even when the change carried no reason, as an empty string —
   *  `omitempty` does not reach inside a struct. Empty is "no reason". */
  reason?: Maybe<string>
}

function toActionEntry(wire: ActionEntryResponse): CaseActionEntry {
  return {
    id: wire.id,
    occurredAt: wire.occurredAt,
    actorName: wire.actorName,
    action: wire.action,
    detail: wire.detail,
  }
}

/** Narrows a wire value to what the feed renders: the marker survives, `null`
 *  and `''` become absent. */
function toValue(value: Maybe<string | null> | undefined): Maybe<string> | undefined {
  if (value === null || value === undefined || value === '') return undefined
  return value
}

function toAuditEntry(wire: AuditEntryResponse): AuditEntry {
  return {
    id: wire.id,
    actorName: wire.actorName,
    // `at`, not `occurredAt`: this type has said `at` since the section first
    // rendered it, and one mapper renaming a field beats every reader of the
    // type learning which of the two names finance happens to use.
    at: wire.occurredAt,
    recordKind: wire.recordKind,
    field: wire.field,
    oldValue: toValue(wire.oldValue),
    // A new value that is somehow absent still renders — as the fallback dash —
    // rather than taking the row out of the feed. An entry with a missing half
    // is a data problem to see, not one to hide.
    newValue: toValue(wire.newValue) ?? '',
    reason: toValue(wire.reason),
  }
}

/** The two sources as one reverse-chronological feed.
 *
 *  Exported and pure so the ordering is provable without a component: the ONE
 *  thing a merge can get wrong is the interleaving, and the interleaving is
 *  invisible in any fixture where the two sources happen not to overlap. */
export function mergeCaseHistory(
  actions: CaseActionEntry[] | undefined,
  audit: AuditEntry[] | undefined,
): CaseHistoryEvent[] {
  const events: CaseHistoryEvent[] = [
    ...(actions ?? []).map(
      (entry): CaseHistoryEvent => ({ source: 'action', at: entry.occurredAt, entry }),
    ),
    ...(audit ?? []).map(
      (entry): CaseHistoryEvent => ({ source: 'audit', at: entry.at, entry }),
    ),
  ]

  // By instant rather than by string: both sources send RFC 3339 in UTC today,
  // so a lexical sort would agree — right up to the first entry that arrives
  // with an offset, which would then sort by the characters of its zone.
  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
}

export interface CaseHistoryResult {
  events: CaseHistoryEvent[]
  /** The two queries, UNMERGED. The section renders one source while the other
   *  shows an inline notice with a retry, which it can only do while their
   *  loading and error states are still tellable apart. */
  actions: UseQueryResult<CaseActionEntry[]>
  audit: UseQueryResult<AuditEntry[]>
  /** Whether the feed is still waiting on a source it asked for. Not
   *  `actions.isPending || audit.isPending`: a trail that is not asked for
   *  (`withAudit: false`) is a disabled query, and a disabled query is pending
   *  for ever — the section would hold its skeleton over a finished log. */
  loading: boolean
  /** The trail came back at exactly the cap, so there is older money history
   *  the feed is not showing. */
  auditTruncated: boolean
  /** The log came back at the server's cap, so there are older acts the feed is
   *  not showing. Announced separately from the trail's: the two caps are two
   *  different numbers chosen in two different places, and one sentence
   *  covering both would be wrong the day either moves. */
  actionsTruncated: boolean
}

export interface CaseHistoryOptions {
  /** Whether to read the financial audit trail beside the action log. Off for
   *  a case whose money this reader may not reach — the record's
   *  `moneyUnavailable: "forbidden"` (story B3, fix round 1): the trail is
   *  money, the server answers it with a 404, and a refusal the client could
   *  have foreseen is not a failure to announce. Defaults to on. */
  withAudit?: boolean
}

export function useCaseHistory(
  caseId: string,
  { withAudit = true }: CaseHistoryOptions = {},
): CaseHistoryResult {
  const actions = useQuery<CaseActionEntry[]>({
    queryKey: queryKeys.cases.history(caseId),
    queryFn: async () => {
      if (USE_MOCKS) return MOCK_CASE_ACTIONS[caseId] ?? []
      const { items } = await api.get<{ items: ActionEntryResponse[] }>(
        `/cases/${caseId}/history`,
      )
      return items.map(toActionEntry)
    },
  })

  const audit = useQuery<AuditEntry[]>({
    /* Derived from the case's detail key rather than spelled fresh, so
       invalidating the case refreshes the trail with everything else — a
       recorded payment belongs in this feed the moment the money card shows
       it. Assembled here rather than in the key factory because the endpoint
       is finance's and this section is its only reader: one caller is not yet
       a shared resource. */
    queryKey: [...queryKeys.cases.detail(caseId), 'audit'],
    enabled: withAudit,
    queryFn: async () => {
      if (USE_MOCKS) return MOCK_CASE_AUDIT[caseId] ?? []
      const { items } = await api.get<{ items: AuditEntryResponse[] }>(
        `/cases/${caseId}/audit`,
        { params: { limit: AUDIT_LIMIT } },
      )
      return items.map(toAuditEntry)
    },
  })

  /* A trail not asked for contributes nothing, even if the cache still holds
     one from before the reader's money reach changed: the feed shows what this
     reader may read NOW. */
  const trail = withAudit ? audit.data : undefined

  const events = useMemo(
    () => mergeCaseHistory(actions.data, trail),
    [actions.data, trail],
  )

  return {
    events,
    actions,
    audit,
    loading: actions.isPending || (withAudit && audit.isPending),
    auditTruncated: (trail?.length ?? 0) >= AUDIT_LIMIT,
    actionsTruncated: (actions.data?.length ?? 0) >= ACTION_LIMIT,
  }
}
